import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Ban,
  Banknote,
  Calculator,
  Eye,
  FileCheck,
  FileDown,
  FolderOpen,
  History,
  Pencil,
  Send,
  StickyNote,
  Undo2,
} from 'lucide-react';
import { BoutonPrincipal, BoutonSecondaire, Chargement, MessageErreur, Pastille, PastilleSite } from '../ui/Primitives';
import { Modale } from '../ui/Modale';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import { aujourdhui } from '../../lib/dates-civiles';
import { urlFichierCertificat } from '../../lib/useCertificats';
import { formaterCentimes } from '../../lib/useFinancements';
import {
  urlApercuFacture,
  urlOriginalFacture,
  useAjouterNoteSuivi,
  useFicheFacture,
  useNoterEnvoi,
  useNoterRejet,
  useRetirerEnvoi,
  useRetirerNoteSuivi,
  useRetirerRejet,
  type CanalEnvoi,
  type EvenementSuivi,
  type FicheFacture as Fiche,
  type Reglement,
} from '../../lib/useFactures';
import {
  CLASSE_CHAMP,
  CLASSE_LIEN_BOUTON,
  Champ,
  ModaleReglement,
  ModaleRetraitReglement,
  Reglements,
  messageRefus,
  quantiteEtUnite,
} from '../financements/FacturesDuContrat';

function jourFrancais(iso: string): string {
  const [annee, mois, jour] = iso.slice(0, 10).split('-');
  return `${jour}/${mois}/${annee}`;
}

function dateHeure(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Indian/Reunion',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso));
}

/** « 23/10/2026 à 09:42 », à l'heure de La Réunion. */
function horodatage(iso: string): string {
  const instant = new Date(iso);
  const jour = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Indian/Reunion',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(instant);
  const heure = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Indian/Reunion',
    hour: '2-digit',
    minute: '2-digit',
  }).format(instant);
  return `${jour} à ${heure}`;
}

/** « 1 848,00 € » d'un montant rendu par l'API, « 1848.00 ». */
function montant(decimal: string): string {
  return formaterCentimes(Math.round(Number(decimal) * 100));
}

/** Jour civil réunionnais d'un instant, `AAAA-MM-JJ`, pour borner un champ date. */
function jourCivil(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Indian/Reunion' }).format(new Date(iso));
}

type Geste =
  | { type: 'envoi' }
  | { type: 'retraitEnvoi' }
  | { type: 'rejet' }
  | { type: 'retraitRejet' }
  | { type: 'reglement' }
  | { type: 'retraitReglement'; reglement: Reglement }
  | { type: 'retraitNote'; evenement: EvenementSuivi };

/**
 * La fiche d'une facture ou d'un avoir, ouverte depuis l'écran « Factures »
 * (TF-422) : son contenu tel qu'imprimé, ce qu'elle a figé à l'émission, son
 * envoi au client — noté à la main, avec son rejet et son renvoi —, ses
 * règlements, et son fil de suivi : les gestes lus dans le journal et les notes
 * du pôle (TF-403 rouverte, « suivre sans Tiime »).
 *
 * Préparer, recalculer, émettre et établir un avoir restent dans le bloc
 * Financement du dossier, qui lit son contrat ou son accord : la fiche y mène.
 * Ce qui empêche chaque geste vient de l'API.
 *
 * Une fenêtre de geste (envoi, règlement) prend la place de la fiche le temps
 * de la saisie, puis la fiche revient : deux fenêtres superposées se
 * fermeraient ensemble à la touche Échap.
 */
export function FicheFacture({ id, onFermer }: { id: string; onFermer: () => void }): JSX.Element {
  const { data, isLoading, error } = useFicheFacture(id);
  const [geste, setGeste] = useState<Geste | null>(null);
  const fermerGeste = () => setGeste(null);

  if (data && geste) {
    const { facture } = data;
    if (geste.type === 'envoi') return <ModaleEnvoi fiche={data} onFermer={fermerGeste} />;
    if (geste.type === 'retraitEnvoi') return <ModaleRetraitEnvoi fiche={data} onFermer={fermerGeste} />;
    if (geste.type === 'rejet') return <ModaleRejet fiche={data} onFermer={fermerGeste} />;
    if (geste.type === 'retraitRejet') return <ModaleRetraitRejet fiche={data} onFermer={fermerGeste} />;
    if (geste.type === 'retraitNote') {
      return <ModaleRetraitNote fiche={data} evenement={geste.evenement} onFermer={fermerGeste} />;
    }
    if (geste.type === 'reglement') {
      return <ModaleReglement facture={facture} modes={data.referentiel.modesReglement} onFermer={fermerGeste} />;
    }
    return <ModaleRetraitReglement facture={facture} reglement={geste.reglement} onFermer={fermerGeste} />;
  }

  const facture = data?.facture;
  const titre = facture ? (facture.numero ?? `${facture.natureLibelle} en préparation`) : 'Facture';
  return (
    <Modale
      ouverte
      titre={titre}
      sousTitre={facture ? `${facture.natureLibelle} · ${facture.objetLibelle} · période ${facture.periode.texte}` : undefined}
      onFermer={onFermer}
      largeur="max-w-3xl"
    >
      {isLoading && <Chargement libelle="Chargement de la facture…" />}
      {error && (
        <div className="p-4 sm:p-6">
          <MessageErreur message={error instanceof ErreurApi ? error.message : 'La facture n’a pas pu être chargée.'} />
        </div>
      )}
      {data && <Contenu fiche={data} onGeste={setGeste} />}
    </Modale>
  );
}

function Contenu({ fiche, onGeste }: { fiche: Fiche; onGeste: (geste: Geste) => void }): JSX.Element {
  const { facture, apprenant, mentions, envoi } = fiche;
  const brouillon = facture.statut === 'BROUILLON';
  const avoir = facture.nature === 'AVOIR';

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <Pastille texte={facture.situation.libelle} ton={facture.situation.ton} />
            {envoi && <Pastille texte={envoi.libelle} ton={envoi.ton} />}
          </div>
          {facture.situation.detail && <p className="text-xs text-codialis-gray">{facture.situation.detail}</p>}
        </div>
        <div className="text-right">
          <div className="font-display text-2xl font-extrabold tabular-nums text-codialis-navy">
            {avoir ? `− ${facture.totaux.ttc}` : facture.totaux.ttc}
          </div>
          <div className="text-[11px] text-codialis-gray">TTC</div>
        </div>
      </div>

      <Section titre="Apprenant">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="font-semibold text-codialis-navy">
              {apprenant.prenom} {apprenant.nom}
            </p>
            {apprenant.formation && <p className="break-words text-xs text-codialis-gray">{apprenant.formation}</p>}
          </div>
          <PastilleSite site={apprenant.siteCode} />
        </div>
      </Section>

      {mentions ? (
        <Section titre="Client et mentions figées à l’émission">
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <Mention terme="Client">
              {mentions.client.nom ?? '—'}
              {mentions.client.typeLibelle && (
                <span className="block text-xs text-codialis-gray">{mentions.client.typeLibelle}</span>
              )}
            </Mention>
            <Mention terme="Adresse">
              <span className="whitespace-pre-line">{mentions.client.adresse ?? '—'}</span>
            </Mention>
            <Mention terme="SIRET">
              <span className="font-mono">{mentions.client.siret ?? '—'}</span>
            </Mention>
            <Mention terme="Référence de prise en charge">
              <span className="break-all font-mono">{mentions.reference ?? 'Aucune'}</span>
            </Mention>
            {mentions.numeroProForma && (
              <Mention terme="N° de pro forma">
                <span className="break-all font-mono">{mentions.numeroProForma}</span>
              </Mention>
            )}
            <Mention terme="Apprenant(e)">{mentions.apprenant ?? '—'}</Mention>
            <Mention terme="Formation">{mentions.formation ?? '—'}</Mention>
            <Mention terme="Dates de formation">{mentions.datesFormation ?? '—'}</Mention>
            <Mention terme="Heures réalisées">{mentions.heures ?? '—'}</Mention>
          </dl>
        </Section>
      ) : (
        <Section titre="Client">
          <p className="text-sm text-codialis-navy">{facture.client ?? 'Aucun payeur désigné'}</p>
          <p className="mt-1 text-xs text-codialis-gray">
            Rien n’est figé avant l’émission : le client suit le payeur de la prise en charge, et la facture se
            prépare, se recalcule et s’émet depuis le bloc Financement du dossier.
          </p>
          {facture.refus.emission && (
            <p className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
              {facture.refus.emission}
            </p>
          )}
          {facture.calcul?.aRevoir && (
            <p className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
              {facture.calcul.aRevoir}
            </p>
          )}
        </Section>
      )}

      <Section titre="Lignes">
        <ul className="divide-y divide-gray-100">
          {facture.lignes.map((ligne, rang) => (
            <li key={rang} className="flex items-baseline justify-between gap-3 py-1.5 text-sm">
              <span className="min-w-0 break-words text-codialis-navy">
                {ligne.libelle}
                <span className="block text-xs text-codialis-gray">
                  {quantiteEtUnite(ligne.quantite, ligne.uniteLibelle)}
                  {ligne.prixUnitaire ? ` × ${montant(ligne.prixUnitaire)} HT` : ''} · TVA{' '}
                  {ligne.tauxTva.replace('.', ',')} %
                </span>
              </span>
              <span className="shrink-0 tabular-nums text-codialis-navy">{montant(ligne.montant)}</span>
            </li>
          ))}
        </ul>
        <dl className="mt-2 ml-auto w-full max-w-xs space-y-0.5 border-t border-gray-100 pt-2 text-sm">
          <Total terme="Total HT" valeur={facture.totaux.ht} />
          <Total terme="TVA" valeur={facture.totaux.tva} />
          <Total terme="Total TTC" valeur={facture.totaux.ttc} fort />
        </dl>
        {facture.calcul && (
          <p className="mt-2 flex gap-1.5 rounded-lg bg-codialis-sand px-3 py-2 text-[11px] text-codialis-gray">
            <Calculator className="mt-px h-3.5 w-3.5 shrink-0 text-codialis-sky" aria-hidden />
            <span className="min-w-0 break-words">
              <span className="font-semibold text-codialis-navy">{facture.calcul.titre} : </span>
              {facture.calcul.detail}
              {facture.calcul.corrige ? ` — montant corrigé à la main (le calcul proposait ${facture.calcul.propose}).` : ''}
            </span>
          </p>
        )}
      </Section>

      {!brouillon && (
        <Section titre="Émission">
          <div className="space-y-0.5 text-xs text-codialis-gray">
            {facture.emiseLe && (
              <p>
                Émise le {dateHeure(facture.emiseLe)}
                {facture.emisePar ? ` par ${facture.emisePar}` : ''}
                {facture.dateEcheancePaiement ? ` · à régler avant le ${jourFrancais(facture.dateEcheancePaiement)}` : ''}
              </p>
            )}
            {facture.origine && (
              <p>
                Annule la facture <span className="font-mono">{facture.origine.numero}</span>
                {facture.motif ? ` — ${facture.motif}` : ''}
              </p>
            )}
            {facture.avoirs.length > 0 && (
              <p>
                Avoir{facture.avoirs.length > 1 ? 's' : ''} :{' '}
                <span className="font-mono">{facture.avoirs.map((un) => un.numero).join(', ')}</span>
              </p>
            )}
            {facture.certificat && (
              <p className={`flex gap-1.5 ${facture.certificat.id ? '' : 'font-semibold text-amber-800'}`}>
                <FileCheck className="mt-px h-3.5 w-3.5 shrink-0 text-codialis-sky" aria-hidden />
                <span className="min-w-0 break-words">
                  {facture.certificat.texte}
                  {facture.certificat.id && (
                    <>
                      {' '}
                      <a
                        href={urlFichierCertificat(facture.certificat.id)}
                        target="_blank"
                        rel="noreferrer"
                        className="font-semibold text-codialis-sky hover:underline"
                      >
                        Ouvrir le certificat
                      </a>
                    </>
                  )}
                </span>
              </p>
            )}
          </div>
        </Section>
      )}

      {envoi?.rejet && (
        <Section titre="Envoi au client">
          <div className="space-y-3">
            <div className="rounded-xl bg-red-50 px-3 py-2 text-sm">
              <p className="flex gap-1.5 font-semibold text-red-800">
                <Ban className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span className="min-w-0 break-words">{envoi.rejet.texte}</span>
              </p>
              {envoi.rejet.par && <p className="mt-0.5 text-[11px] text-red-700">Noté par {envoi.rejet.par}</p>}
            </div>
            {envoi.le && (
              <p className="text-xs text-codialis-gray">
                Envoi rejeté : le {jourFrancais(envoi.le)}
                {envoi.canalLibelle ? ` · ${envoi.canalLibelle}` : ''}
                {envoi.reference ? ` · réf. ${envoi.reference}` : ''}. Une fois la pièce renvoyée, notez le renvoi.
              </p>
            )}
            <div className="flex flex-wrap items-center justify-end gap-2">
              {fiche.refus.retraitRejet === null && (
                <BoutonSecondaire onClick={() => onGeste({ type: 'retraitRejet' })}>
                  <Undo2 className="mr-1.5 h-4 w-4" aria-hidden />
                  Retirer le rejet
                </BoutonSecondaire>
              )}
              {fiche.refus.envoi === null && (
                <BoutonPrincipal onClick={() => onGeste({ type: 'envoi' })}>
                  <Send className="mr-1.5 h-4 w-4" aria-hidden />
                  Noter le renvoi
                </BoutonPrincipal>
              )}
            </div>
          </div>
        </Section>
      )}

      {envoi && !envoi.rejet && (
        <Section titre="Envoi au client">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 space-y-0.5 text-sm">
              <p className={envoi.etat === 'A_ENVOYER' ? 'font-semibold text-amber-800' : 'text-codialis-navy'}>
                {envoi.detail ?? 'Pas encore envoyée : l’envoi se fait hors de la plateforme, puis se note ici.'}
              </p>
              {envoi.par && <p className="text-[11px] text-codialis-gray">Noté par {envoi.par}</p>}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {envoi.le ? (
                <>
                  {fiche.refus.retraitEnvoi === null && (
                    <BoutonSecondaire onClick={() => onGeste({ type: 'retraitEnvoi' })}>
                      <Undo2 className="mr-1.5 h-4 w-4" aria-hidden />
                      Retirer
                    </BoutonSecondaire>
                  )}
                  {fiche.refus.envoi === null && (
                    <BoutonSecondaire onClick={() => onGeste({ type: 'envoi' })}>
                      <Pencil className="mr-1.5 h-4 w-4" aria-hidden />
                      Corriger
                    </BoutonSecondaire>
                  )}
                  {fiche.refus.rejet === null && (
                    <BoutonSecondaire onClick={() => onGeste({ type: 'rejet' })}>
                      <Ban className="mr-1.5 h-4 w-4" aria-hidden />
                      Noter un rejet
                    </BoutonSecondaire>
                  )}
                </>
              ) : (
                fiche.refus.envoi === null && (
                  <BoutonPrincipal onClick={() => onGeste({ type: 'envoi' })}>
                    <Send className="mr-1.5 h-4 w-4" aria-hidden />
                    Noter l’envoi
                  </BoutonPrincipal>
                )
              )}
            </div>
          </div>
        </Section>
      )}

      {facture.solde && (
        <Section titre="Paiement">
          <Reglements
            facture={facture}
            solde={facture.solde}
            onRetirer={(reglement) => onGeste({ type: 'retraitReglement', reglement })}
          />
          {facture.refus.reglement === null && (
            <div className="mt-2 flex justify-end">
              <BoutonPrincipal onClick={() => onGeste({ type: 'reglement' })}>
                <Banknote className="mr-1.5 h-4 w-4" aria-hidden />
                Saisir un règlement
              </BoutonPrincipal>
            </div>
          )}
        </Section>
      )}

      <FilDeSuivi fiche={fiche} onRetirerNote={(evenement) => onGeste({ type: 'retraitNote', evenement })} />

      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 pt-4">
        <Link to={`/adm-dossier/${apprenant.id}`} className={CLASSE_LIEN_BOUTON}>
          <FolderOpen className="mr-1.5 h-4 w-4" aria-hidden />
          Ouvrir le dossier
        </Link>
        {brouillon && (
          <a href={urlApercuFacture(facture.id)} target="_blank" rel="noreferrer" className={CLASSE_LIEN_BOUTON}>
            <Eye className="mr-1.5 h-4 w-4" aria-hidden />
            Aperçu
          </a>
        )}
        {facture.fichier && (
          <a href={urlOriginalFacture(facture.id)} target="_blank" rel="noreferrer" className={CLASSE_LIEN_BOUTON}>
            <FileDown className="mr-1.5 h-4 w-4" aria-hidden />
            Télécharger l’original
          </a>
        )}
      </div>
    </div>
  );
}

function Section({ titre, children }: { titre: string; children: ReactNode }): JSX.Element {
  return (
    <section aria-label={titre} className="rounded-xl border border-gray-100 p-3 sm:p-4">
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-codialis-gray">{titre}</h3>
      {children}
    </section>
  );
}

function Mention({ terme, children }: { terme: string; children: ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-codialis-gray">{terme}</dt>
      <dd className="break-words text-codialis-navy">{children}</dd>
    </div>
  );
}

function Total({ terme, valeur, fort = false }: { terme: string; valeur: string; fort?: boolean }): JSX.Element {
  return (
    <div className={`flex justify-between gap-3 ${fort ? 'font-bold text-codialis-navy' : 'text-codialis-gray'}`}>
      <dt>{terme}</dt>
      <dd className="tabular-nums">{valeur}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Envoi noté à la main
// ---------------------------------------------------------------------------

function ModaleEnvoi({ fiche, onFermer }: { fiche: Fiche; onFermer: () => void }): JSX.Element {
  const toast = useToast();
  const noter = useNoterEnvoi();
  const { facture, envoi, referentiel } = fiche;
  // Sur une pièce rejetée, le geste est le renvoi : un nouveau dépôt, après le rejet.
  const renvoi = envoi?.rejet ?? null;
  const correction = !renvoi && envoi?.le !== null && envoi?.le !== undefined;
  const [date, setDate] = useState(renvoi ? aujourdhui() : (envoi?.le ?? aujourdhui()));
  const [canal, setCanal] = useState<CanalEnvoi>(envoi?.canal ?? referentiel.canaux[0]?.valeur ?? 'PORTAIL_FINANCEUR');
  const [reference, setReference] = useState(renvoi ? '' : (envoi?.reference ?? ''));
  const [erreur, setErreur] = useState<string | null>(null);
  const emiseLe = facture.emiseLe ? jourCivil(facture.emiseLe) : undefined;
  const titre = renvoi ? 'Noter le renvoi' : correction ? 'Corriger l’envoi' : 'Noter l’envoi';

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setErreur(null);
    noter.mutate(
      { id: facture.id, saisie: { date, canal, reference } },
      {
        onSuccess: () => {
          toast(renvoi ? 'Renvoi noté.' : correction ? 'Envoi corrigé.' : 'Envoi noté.');
          onFermer();
        },
        onError: (refus) => setErreur(messageRefus(refus, 'L’envoi n’a pas pu être noté.')),
      },
    );
  };

  return (
    <Modale
      ouverte
      titre={titre}
      sousTitre={`${facture.natureLibelle} ${facture.numero ?? ''} · ${facture.client ?? ''}`}
      onFermer={onFermer}
      largeur="max-w-lg"
    >
      <form onSubmit={soumettre} className="space-y-4 p-4 sm:p-6" aria-label="Envoi au client">
        <p className="text-sm text-codialis-navy">
          {renvoi
            ? `${renvoi.texte}. Notez le nouveau dépôt de la pièce : le rejet sera effacé, et gardé dans le fil de suivi.`
            : 'L’envoi se fait hors de la plateforme — portail de l’OPCO, Chorus Pro, mail, courrier. Notez-le ici pour savoir ce qui reste à envoyer.'}
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Champ libelle={renvoi ? 'Renvoyée le' : 'Envoyée le'} id="envoi-date">
            <input
              id="envoi-date"
              type="date"
              required
              value={date}
              min={renvoi ? renvoi.le : emiseLe}
              max={aujourdhui()}
              onChange={(e) => {
                setDate(e.target.value);
                setErreur(null);
              }}
              className={CLASSE_CHAMP}
            />
          </Champ>
          <Champ libelle="Canal" id="envoi-canal">
            <select
              id="envoi-canal"
              value={canal}
              onChange={(e) => setCanal(e.target.value as CanalEnvoi)}
              className={CLASSE_CHAMP}
            >
              {referentiel.canaux.map((un) => (
                <option key={un.valeur} value={un.valeur}>
                  {un.libelle}
                </option>
              ))}
            </select>
          </Champ>
        </div>
        <Champ libelle="Référence de dépôt (facultative)" id="envoi-reference">
          <input
            id="envoi-reference"
            maxLength={100}
            value={reference}
            placeholder="N° de dépôt, référence du courrier"
            onChange={(e) => setReference(e.target.value)}
            className={CLASSE_CHAMP}
          />
        </Champ>
        {erreur && (
          <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            {erreur}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <BoutonSecondaire onClick={onFermer} disabled={noter.isPending}>
            Annuler
          </BoutonSecondaire>
          <BoutonPrincipal type="submit" disabled={noter.isPending}>
            {noter.isPending ? 'Enregistrement…' : correction ? 'Enregistrer la correction' : titre}
          </BoutonPrincipal>
        </div>
      </form>
    </Modale>
  );
}

function ModaleRetraitEnvoi({ fiche, onFermer }: { fiche: Fiche; onFermer: () => void }): JSX.Element {
  const toast = useToast();
  const retirer = useRetirerEnvoi();
  const [erreur, setErreur] = useState<string | null>(null);
  const { facture, envoi } = fiche;

  const confirmer = (): void => {
    setErreur(null);
    retirer.mutate(facture.id, {
      onSuccess: () => {
        toast('Envoi retiré.');
        onFermer();
      },
      onError: (refus) => setErreur(messageRefus(refus, 'L’envoi n’a pas pu être retiré.')),
    });
  };

  return (
    <Modale
      ouverte
      titre="Retirer l’envoi"
      sousTitre={`${facture.natureLibelle} ${facture.numero ?? ''}`}
      onFermer={onFermer}
      largeur="max-w-lg"
    >
      <div className="space-y-4 p-4 sm:p-6">
        <p className="text-sm text-codialis-navy">
          {envoi?.detail ?? 'Envoi noté'}. Retiré, il ne compte plus : la pièce redevient « à envoyer ». À faire
          seulement si l’envoi a été noté par erreur ; une date ou un canal faux se corrige.
        </p>
        {erreur && (
          <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            {erreur}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <BoutonSecondaire onClick={onFermer} disabled={retirer.isPending}>
            Annuler
          </BoutonSecondaire>
          <BoutonPrincipal onClick={confirmer} disabled={retirer.isPending}>
            {retirer.isPending ? 'Retrait…' : 'Retirer l’envoi'}
          </BoutonPrincipal>
        </div>
      </div>
    </Modale>
  );
}

// ---------------------------------------------------------------------------
// Rejet par son client
// ---------------------------------------------------------------------------

function ModaleRejet({ fiche, onFermer }: { fiche: Fiche; onFermer: () => void }): JSX.Element {
  const toast = useToast();
  const noter = useNoterRejet();
  const { facture, envoi } = fiche;
  const [date, setDate] = useState(aujourdhui());
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setErreur(null);
    noter.mutate(
      { id: facture.id, saisie: { date, motif } },
      {
        onSuccess: () => {
          toast('Rejet noté : la pièce est à renvoyer.');
          onFermer();
        },
        onError: (refus) => setErreur(messageRefus(refus, 'Le rejet n’a pas pu être noté.')),
      },
    );
  };

  return (
    <Modale
      ouverte
      titre="Noter un rejet"
      sousTitre={`${facture.natureLibelle} ${facture.numero ?? ''} · ${facture.client ?? ''}`}
      onFermer={onFermer}
      largeur="max-w-lg"
    >
      <form onSubmit={soumettre} className="space-y-4 p-4 sm:p-6" aria-label="Rejet par le client">
        <p className="text-sm text-codialis-navy">
          Le client a refusé la pièce — dépôt rejeté par le portail, référence DK erronée, pièce manquante. Elle
          redevient « à renvoyer » jusqu’à ce que le renvoi soit noté.
        </p>
        {envoi?.detail && <p className="text-xs text-codialis-gray">{envoi.detail}</p>}
        <Champ libelle="Rejetée le" id="rejet-date">
          <input
            id="rejet-date"
            type="date"
            required
            value={date}
            min={envoi?.le ?? undefined}
            max={aujourdhui()}
            onChange={(e) => {
              setDate(e.target.value);
              setErreur(null);
            }}
            className={CLASSE_CHAMP}
          />
        </Champ>
        <Champ libelle="Motif du rejet" id="rejet-motif">
          <textarea
            id="rejet-motif"
            required
            minLength={5}
            maxLength={1000}
            rows={3}
            value={motif}
            placeholder="Ce que le financeur reproche à la pièce"
            onChange={(e) => {
              setMotif(e.target.value);
              setErreur(null);
            }}
            className={`${CLASSE_CHAMP} py-2`}
          />
        </Champ>
        {erreur && (
          <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            {erreur}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <BoutonSecondaire onClick={onFermer} disabled={noter.isPending}>
            Annuler
          </BoutonSecondaire>
          <BoutonPrincipal type="submit" disabled={noter.isPending || motif.trim().length < 5}>
            {noter.isPending ? 'Enregistrement…' : 'Noter le rejet'}
          </BoutonPrincipal>
        </div>
      </form>
    </Modale>
  );
}

function ModaleRetraitRejet({ fiche, onFermer }: { fiche: Fiche; onFermer: () => void }): JSX.Element {
  const toast = useToast();
  const retirer = useRetirerRejet();
  const [erreur, setErreur] = useState<string | null>(null);
  const { facture, envoi } = fiche;

  const confirmer = (): void => {
    setErreur(null);
    retirer.mutate(facture.id, {
      onSuccess: () => {
        toast('Rejet retiré.');
        onFermer();
      },
      onError: (refus) => setErreur(messageRefus(refus, 'Le rejet n’a pas pu être retiré.')),
    });
  };

  return (
    <Modale
      ouverte
      titre="Retirer le rejet"
      sousTitre={`${facture.natureLibelle} ${facture.numero ?? ''}`}
      onFermer={onFermer}
      largeur="max-w-lg"
    >
      <div className="space-y-4 p-4 sm:p-6">
        <p className="text-sm text-codialis-navy">
          {envoi?.rejet?.texte ?? 'Rejet noté'}. Retiré, il ne compte plus : la pièce redevient « envoyée ». À faire
          seulement si le rejet a été noté par erreur ; une pièce renvoyée se note par « Noter le renvoi ».
        </p>
        {erreur && (
          <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            {erreur}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <BoutonSecondaire onClick={onFermer} disabled={retirer.isPending}>
            Annuler
          </BoutonSecondaire>
          <BoutonPrincipal onClick={confirmer} disabled={retirer.isPending}>
            {retirer.isPending ? 'Retrait…' : 'Retirer le rejet'}
          </BoutonPrincipal>
        </div>
      </div>
    </Modale>
  );
}

// ---------------------------------------------------------------------------
// Fil de suivi : gestes et notes
// ---------------------------------------------------------------------------

/** Au-delà, le fil se replie sur ses évènements les plus récents. */
const EVENEMENTS_VISIBLES = 6;

function FilDeSuivi({
  fiche,
  onRetirerNote,
}: {
  fiche: Fiche;
  onRetirerNote: (evenement: EvenementSuivi) => void;
}): JSX.Element {
  const toast = useToast();
  const ajouter = useAjouterNoteSuivi();
  const [texte, setTexte] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [toutVoir, setToutVoir] = useState(false);
  const { facture, suivi } = fiche;
  const visibles = toutVoir ? suivi : suivi.slice(0, EVENEMENTS_VISIBLES);

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setErreur(null);
    ajouter.mutate(
      { id: facture.id, texte },
      {
        onSuccess: () => {
          setTexte('');
          toast('Note ajoutée.');
        },
        onError: (refus) => setErreur(messageRefus(refus, 'La note n’a pas pu être ajoutée.')),
      },
    );
  };

  return (
    <Section titre="Suivi">
      <form onSubmit={soumettre} className="space-y-2" aria-label="Ajouter une note de suivi">
        <label htmlFor="note-suivi" className="block text-[11px] font-semibold text-codialis-gray">
          Note de suivi — relance, échange avec le financeur…
        </label>
        <textarea
          id="note-suivi"
          maxLength={2000}
          rows={2}
          value={texte}
          onChange={(e) => {
            setTexte(e.target.value);
            setErreur(null);
          }}
          className={`${CLASSE_CHAMP} py-2`}
        />
        {erreur && (
          <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            {erreur}
          </p>
        )}
        <div className="flex justify-end">
          <BoutonSecondaire type="submit" disabled={ajouter.isPending || texte.trim() === ''}>
            <StickyNote className="mr-1.5 h-4 w-4" aria-hidden />
            {ajouter.isPending ? 'Ajout…' : 'Ajouter la note'}
          </BoutonSecondaire>
        </div>
      </form>

      {suivi.length === 0 ? (
        <p className="mt-3 text-xs text-codialis-gray">Aucun geste ni aucune note sur cette pièce.</p>
      ) : (
        <ol className="mt-3 space-y-3 border-l border-gray-200 pl-4" aria-label="Fil de suivi de la pièce">
          {visibles.map((evenement) => (
            <EvenementDuFil key={evenement.id} evenement={evenement} onRetirer={() => onRetirerNote(evenement)} />
          ))}
        </ol>
      )}
      {suivi.length > EVENEMENTS_VISIBLES && (
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={() => setToutVoir((avant) => !avant)}
            className="min-h-[44px] rounded-xl px-3 text-xs font-semibold text-codialis-sky hover:underline"
          >
            {toutVoir ? 'Replier le fil' : `Voir tout le fil (${suivi.length})`}
          </button>
        </div>
      )}
    </Section>
  );
}

function EvenementDuFil({ evenement, onRetirer }: { evenement: EvenementSuivi; onRetirer: () => void }): JSX.Element {
  const note = evenement.type === 'NOTE';
  const Icone = note ? StickyNote : History;
  return (
    <li className="relative min-w-0">
      <Icone
        className={`absolute -left-[1.4rem] top-0.5 h-3.5 w-3.5 bg-white ${note ? 'text-codialis-sky' : 'text-codialis-gray'}`}
        aria-hidden
      />
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <p className="min-w-0 text-sm font-semibold text-codialis-navy">{evenement.libelle}</p>
        <p className="text-[11px] tabular-nums text-codialis-gray">
          {horodatage(evenement.le)}
          {evenement.auteur ? ` · ${evenement.auteur}` : ''}
        </p>
      </div>
      {evenement.texte && (
        <p
          className={`mt-0.5 whitespace-pre-line break-words text-sm ${
            evenement.retrait ? 'text-codialis-gray line-through' : note ? 'text-codialis-navy' : 'text-codialis-gray'
          }`}
        >
          {evenement.texte}
        </p>
      )}
      {evenement.changements.length > 0 && (
        <ul className="mt-0.5 space-y-0.5 text-xs text-codialis-gray">
          {evenement.changements.map((changement) => (
            <li key={changement.champ} className="break-words">
              {changement.champ} : {changement.avant} → <span className="text-codialis-navy">{changement.apres}</span>
            </li>
          ))}
        </ul>
      )}
      {evenement.retrait && (
        <p className="mt-0.5 text-[11px] text-codialis-gray">
          Retirée le {horodatage(evenement.retrait.le)}
          {evenement.retrait.par ? ` par ${evenement.retrait.par}` : ''}
        </p>
      )}
      {evenement.peutRetirer && (
        <button
          type="button"
          onClick={onRetirer}
          className="mt-0.5 min-h-[32px] rounded-lg text-xs font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[44px]"
        >
          Retirer la note
        </button>
      )}
    </li>
  );
}

function ModaleRetraitNote({
  fiche,
  evenement,
  onFermer,
}: {
  fiche: Fiche;
  evenement: EvenementSuivi;
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const retirer = useRetirerNoteSuivi();
  const [erreur, setErreur] = useState<string | null>(null);
  const { facture } = fiche;

  const confirmer = (): void => {
    if (!evenement.noteId) return;
    setErreur(null);
    retirer.mutate(
      { id: facture.id, noteId: evenement.noteId },
      {
        onSuccess: () => {
          toast('Note retirée.');
          onFermer();
        },
        onError: (refus) => setErreur(messageRefus(refus, 'La note n’a pas pu être retirée.')),
      },
    );
  };

  return (
    <Modale
      ouverte
      titre="Retirer la note"
      sousTitre={`${facture.natureLibelle} ${facture.numero ?? ''}`}
      onFermer={onFermer}
      largeur="max-w-lg"
    >
      <div className="space-y-4 p-4 sm:p-6">
        <p className="whitespace-pre-line break-words rounded-xl bg-codialis-sand px-3 py-2 text-sm text-codialis-navy">
          {evenement.texte}
        </p>
        <p className="text-sm text-codialis-navy">
          Une note ne se corrige pas. Retirée, elle reste au fil de suivi, barrée, avec la date de son retrait.
        </p>
        {erreur && (
          <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            {erreur}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <BoutonSecondaire onClick={onFermer} disabled={retirer.isPending}>
            Annuler
          </BoutonSecondaire>
          <BoutonPrincipal onClick={confirmer} disabled={retirer.isPending}>
            {retirer.isPending ? 'Retrait…' : 'Retirer la note'}
          </BoutonPrincipal>
        </div>
      </div>
    </Modale>
  );
}
